import { CACHE_KEYS, API_ENDPOINTS } from "../../constants/api";
import { type AuthResponse } from "../../types/api";

export type UserRole = "manager" | "verificator" | "operator";

export class TokenManager {
  private authToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;
  private baseURL: string;
  private currentRole: UserRole | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.loadStoredTokens();
  }

  private loadStoredTokens(): void {
    try {
      // Try to load role from sessionStorage first (tab-specific)
      const storedRole = sessionStorage.getItem('current_role');
      if (storedRole) {
        this.currentRole = storedRole as UserRole;
      }
      
      // Load tokens if we have a current role set
      if (this.currentRole) {
        this.authToken = localStorage.getItem(this.getRoleBasedKey(CACHE_KEYS.AUTH_TOKEN));
        this.refreshToken = localStorage.getItem(this.getRoleBasedKey(CACHE_KEYS.REFRESH_TOKEN));
      } else {
        // If no role is set, clear tokens
        this.authToken = null;
        this.refreshToken = null;
      }
    } catch (error) {
      console.warn("Failed to load stored tokens:", error);
    }
  }

  private getRoleBasedKey(baseKey: string): string {
    return this.currentRole ? `${baseKey}:${this.currentRole}` : baseKey;
  }

  public storeTokens(authToken: string, refreshToken: string | undefined, role: UserRole): void {
    try {
      this.authToken = authToken;
      this.currentRole = role;
      
      // Store tokens with role-based keys (no global role storage)
      const authKey = this.getRoleBasedKey(CACHE_KEYS.AUTH_TOKEN);
      const refreshKey = this.getRoleBasedKey(CACHE_KEYS.REFRESH_TOKEN);
      
      localStorage.setItem(authKey, authToken);

      if (refreshToken) {
        this.refreshToken = refreshToken;
        localStorage.setItem(refreshKey, refreshToken);
      }
    } catch (error) {
      console.error("Failed to store tokens:", error);
    }
  }

  public clearTokens(): void {
    this.authToken = null;
    this.refreshToken = null;
    
    // Clear role-based tokens
    if (this.currentRole) {
      localStorage.removeItem(this.getRoleBasedKey(CACHE_KEYS.AUTH_TOKEN));
      localStorage.removeItem(this.getRoleBasedKey(CACHE_KEYS.REFRESH_TOKEN));
    }
    
    localStorage.removeItem(CACHE_KEYS.USER_DATA);
    sessionStorage.removeItem('current_role');
    this.currentRole = null;
  }

  public getAuthToken(): string | null {
    this.loadStoredTokens();
    return this.authToken;
  }

  public isAuthenticated(): boolean {
    this.loadStoredTokens();
    return !!this.authToken;
  }

  public getCurrentRole(): UserRole | null {
    return this.currentRole;
  }

  public setCurrentRole(role: UserRole): void {
    // Only update role if it's different
    if (this.currentRole !== role) {
      this.currentRole = role;
      
      // Store role in sessionStorage (tab-specific)
      sessionStorage.setItem('current_role', role);
      
      // Load tokens for the new role
      this.loadStoredTokens();
    }
  }

  public clearTokensForRole(role: UserRole): void {
    // Clear tokens for a specific role without affecting current session
    const authKey = `${CACHE_KEYS.AUTH_TOKEN}:${role}`;
    const refreshKey = `${CACHE_KEYS.REFRESH_TOKEN}:${role}`;
    
    localStorage.removeItem(authKey);
    localStorage.removeItem(refreshKey);
  }

  public async refreshAuthToken(): Promise<boolean> {
    if (this.isRefreshing) {
      return this.refreshPromise || Promise.resolve(false);
    }

    if (!this.refreshToken) {
      this.clearTokens();
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseURL}${API_ENDPOINTS.AUTH.AUTHENTICATE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: this.refreshToken,
            grant_type: "refresh_token",
          }),
        }
      );

      if (response.ok) {
        const data: AuthResponse = await response.json();
        if (data.success && data.token) {
          this.storeTokens(data.token, data.refresh_token, this.currentRole!);
          return true;
        }
      }

      this.clearTokens();
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      this.clearTokens();
      return false;
    }
  }

  public async checkTokenExpiry(): Promise<boolean> {
    // Simple check - in real implementation, decode JWT and check exp claim
    if (!this.authToken) return true;

    // For now, assume token is valid if present
    // TODO: Implement proper JWT expiry checking
    return false;
  }
}
